import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import PageImage from '../assets/PageImage.svg';
import PageImageDark from '../assets/PageImageDark.svg';
import BrainstormWhiteboard from '../assets/BrainstormWhiteboard.svg';
import BrainstormWhiteboardDark from '../assets/BrainstormWhiteboardDark.svg';
import AITeammate from '../assets/AITeammate.svg';
import AITeammateDark from '../assets/AITeammateDark.svg';
import AddProfilePicture from '../assets/AddProfilePicture.svg';
import AddProfilePictureDark from '../assets/AddProfilePictureDark.svg';
import FollowPeopleAndSpaces from '../assets/FollowPeopleAndSpaces.svg';
import FollowPeopleAndSpacesDark from '../assets/FollowPeopleAndSpacesDark.svg';

import type { GettingStartedCardType } from '../../PersonalizedTemplatesV2Types';

const i18n = defineMessages({
	createPage: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page',
		defaultMessage: 'Create a page',
		description: 'Create page card heading',
	},
	createPageDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page.description',
		defaultMessage: 'Plan projects, take notes, and track tasks.',
		description: 'Create page card description',
	},
	createPageCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.create.page.cta',
		defaultMessage: 'Create page',
		description: 'Create page card cta',
	},
	brainstormOnWhiteboard: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard',
		defaultMessage: 'Brainstorm on a whiteboard',
		description: 'Whiteboard card heading',
	},
	brainstormOnWhiteboardDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard.description',
		defaultMessage: 'Dream up new ideas and visualize your work.',
		description: 'Whiteboard card description',
	},
	brainstormOnWhiteboardCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.brainstorm.on.whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description: 'Whiteboard card cta',
	},
	followPeopleAndSpaces: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces',
		defaultMessage: 'Follow people and spaces',
		description: 'Follow people and spaces card heading',
	},
	followPeopleAndSpacesDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces.description',
		defaultMessage: 'Customize your following feed to stay up to date with what matters to you.',
		description: 'Follow people and spaces card description',
	},
	followPeopleAndSpacesCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.follow.people.and.spaces.cta',
		defaultMessage: 'Customize feed',
		description: 'Follow people and spaces card cta',
	},
	meetAITeammate: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate',
		defaultMessage: 'Meet your AI teammate',
		description: 'AI teammate card heading',
	},
	meetAITeammateDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate.description',
		defaultMessage:
			'Rovo connects your knowledge and streamlines work, so you can focus on what matters.',
		description: 'AI teammate card description',
	},
	meetAITeammateCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.meet.your.ai.teammate.cta',
		defaultMessage: 'Chat with Rovo',
		description: 'AI teammate card cta',
	},
	addProfilePicture: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture',
		defaultMessage: 'Add your profile photo',
		description: 'Add profile picture card heading',
	},
	addProfilePictureDescription: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture.description',
		defaultMessage: 'Welcome your teammates to Confluence with a familiar face.',
		description: 'Add profile picture card description',
	},
	addProfilePictureCTA: {
		id: 'experiment-personalized-templates-v2.nth-user.add.profile.picture.cta',
		defaultMessage: 'Add photo',
		description: 'Add profile picture card cta',
	},
	completedCTAText: {
		id: 'experiment-personalized-templates-v2.nth-user.completed.cta',
		defaultMessage: 'Completed',
		description: 'Text to show the action is completed when CTA is clicked',
	},
	dismissLabel: {
		id: 'experiment-personalized-templates-v2.nth-user.dismiss.label',
		defaultMessage: 'Dismiss',
		description: 'Label for dismiss button',
	},
});

// Onboarding actions data
export const NTH_USER_ONBOARDING_ACTIONS_DATA: GettingStartedCardType[] = [
	{
		id: 'createPage',
		image: { src: PageImage, srcDark: PageImageDark },
		heading: <FormattedMessage {...i18n.createPage} />,
		description: <FormattedMessage {...i18n.createPageDescription} />,
		ctaText: <FormattedMessage {...i18n.createPageCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {}, // This will be handled by the card's onDismiss prop
	},
	{
		id: 'brainstormOnWhiteboard',
		image: { src: BrainstormWhiteboard, srcDark: BrainstormWhiteboardDark },
		heading: <FormattedMessage {...i18n.brainstormOnWhiteboard} />,
		description: <FormattedMessage {...i18n.brainstormOnWhiteboardDescription} />,
		ctaText: <FormattedMessage {...i18n.brainstormOnWhiteboardCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'followPeopleAndSpaces',
		image: { src: FollowPeopleAndSpaces, srcDark: FollowPeopleAndSpacesDark },
		heading: <FormattedMessage {...i18n.followPeopleAndSpaces} />,
		description: <FormattedMessage {...i18n.followPeopleAndSpacesDescription} />,
		ctaText: <FormattedMessage {...i18n.followPeopleAndSpacesCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'meetAITeammate',
		image: { src: AITeammate, srcDark: AITeammateDark },
		heading: <FormattedMessage {...i18n.meetAITeammate} />,
		description: <FormattedMessage {...i18n.meetAITeammateDescription} />,
		ctaText: <FormattedMessage {...i18n.meetAITeammateCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
	{
		id: 'addProfilePicture',
		image: { src: AddProfilePicture, srcDark: AddProfilePictureDark },
		heading: <FormattedMessage {...i18n.addProfilePicture} />,
		description: <FormattedMessage {...i18n.addProfilePictureDescription} />,
		ctaText: <FormattedMessage {...i18n.addProfilePictureCTA} />,
		ctaCompletedText: <FormattedMessage {...i18n.completedCTAText} />,
		dismissTooltip: <FormattedMessage {...i18n.dismissLabel} />,
		onDismiss: () => {},
	},
];
